import {hookToAttributeSelector} from '@wix/panda-js-utils'
import {BI_ORIGINS, PlaceWithTicketInfo, isAvailableDonationTicket} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {Text} from '@wix/wix-ui-tpa/cssVars'
import {useEnvironment, useTranslation} from '@wix/yoshi-flow-editor'
import React, {useEffect, useMemo, useState} from 'react'
import {useAppActions} from '../../../../hooks/runtime'
import {isPlaceArea} from '../../../../selectors/seating/place'
import {ErrorSection} from '../../../event-details/tickets-picker-new/error-section'
import {SlideInModal} from '../../slide-in-modal'
import {AccessibilityModeButton} from '../accessibility-mode-button'
import {EventInfo} from '../event-info'
import {Summary} from '../summary'
import {SummaryContainer} from '../summary-container'
import {Ticket} from '../ticket'
import {TicketsListContainer} from '../tickets-list-container'
import {TicketsProps} from './interfaces'
import {classes} from './tickets.st.css'

export const Tickets = ({
  show,
  placesInBasket,
  showAccessibilityMode,
  onBackClick,
  onRemoveClick,
  onCheckoutClick,
  onTicketMoreInfoClick,
  placesInBasketCount,
  setForcedPopoverPlanPlaceId,
}: TicketsProps) => {
  const {isMobile} = useEnvironment()
  const {t} = useTranslation()
  const desktop = !isMobile
  const {setAreaCounterValue} = useAppActions()
  const [ticketSelectionError, setTicketSelectionError] = useState(false)

  useEffect(() => {
    setTicketSelectionError(false)
  }, [placesInBasket])

  const handleTicketOnClick = (currentPlace: PlaceWithTicketInfo) => {
    onRemoveClick(currentPlace)
    setForcedPopoverPlanPlaceId?.(null)
    setAreaCounterValue({placeId: currentPlace.id, count: 1})
  }

  const onTicketSelectionError = () => {
    setTicketSelectionError(true)
  }

  const circularFocusStart = useMemo(() => {
    if (isMobile) {
      return null
    }

    if (!placesInBasketCount) {
      return hookToAttributeSelector(DH.ACCESSIBILITY_MODE_BUTTON)
    }

    if (isPlaceArea(placesInBasket[0])) {
      return `${hookToAttributeSelector(DH.TICKET_AREA_COUNTER)} input`
    }

    if (isAvailableDonationTicket(placesInBasket[0].ticket)) {
      return `input${hookToAttributeSelector(DH.DONATION_INPUT)}`
    }

    return hookToAttributeSelector(DH.ADD)
  }, [placesInBasketCount, placesInBasket, isMobile])

  return (
    <SlideInModal
      id="tickets"
      dataHook={DH.SEATING_BASKET}
      backButtonText={showAccessibilityMode ? t('seatings_backToList') : t('seatings_backToMap')}
      show={show}
      animations={false}
      onBackClick={onBackClick}
      focus={isMobile ? null : DH.EVENT_INFO}
      circularFocusStart={circularFocusStart}
      circularFocusEnd={isMobile ? hookToAttributeSelector(DH.CHECKOUT) : null}
      immediate={isMobile}
      ariaModal={isMobile}
    >
      <EventInfo />
      <Text className={classes.title} tagName="h2">
        {t('seating_basketCount', {count: placesInBasketCount})}
      </Text>
      {desktop && !placesInBasketCount ? (
        <Text className={classes.subtitle} tagName="p">
          {t('seatings_tickets_emptyState_description')}
        </Text>
      ) : null}
      <TicketsListContainer basketView>
        {isMobile && !placesInBasketCount ? (
          <Text className={classes.mobileEmptyState}>{t('seatings_tickets_emptyState_description')}</Text>
        ) : null}
        {placesInBasket.map((place, index) => (
          <Ticket
            key={`${index}-${place.id}`}
            place={place}
            onClick={handleTicketOnClick}
            onRemoveClick={onRemoveClick}
            onTicketMoreInfoClick={onTicketMoreInfoClick}
            setForcedPopoverPlanPlaceId={setForcedPopoverPlanPlaceId}
            basketView={true}
            origin={BI_ORIGINS.SEATINGS_MODAL_BASKET}
          />
        ))}
      </TicketsListContainer>
      {desktop ? <AccessibilityModeButton /> : null}
      <SummaryContainer inTickets={true}>
        {ticketSelectionError && <ErrorSection className={classes.notificationWrapper} />}
        <Summary
          inTickets={true}
          onCheckoutClick={onCheckoutClick}
          onBackClick={onBackClick}
          onTicketSelectionError={onTicketSelectionError}
        />
      </SummaryContainer>
    </SlideInModal>
  )
}
